import { useState } from 'react';
import { api } from '@/api/client';
import type { Member } from '@/types';

interface SetPasswordModalProps {
  member: Member;
  onClose: () => void;
  onDone: (message: string, type: 'success' | 'error') => void;
}

const ORG_ID = () => localStorage.getItem('hr_org_id');

function generatePassword(): string {
  // Без похожих символов (0/O, 1/l) — пароль диктуют голосом и переписывают руками
  const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = new Uint32Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => chars[b % chars.length]).join('');
}

export default function SetPasswordModal({ member, onClose, onDone }: SetPasswordModalProps) {
  const [password, setPassword] = useState(generatePassword);
  const [sendEmail, setSendEmail] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Не удалось скопировать — выделите пароль и скопируйте вручную');
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    if (password.trim().length < 8) {
      setError('Пароль должен быть не короче 8 символов');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await api.post<{ email_sent: boolean | null }>(
        `/organizations/${ORG_ID()}/members/${member.id}/set-password`,
        { password: password.trim(), send_email: sendEmail }
      );
      if (sendEmail && res.email_sent === false) {
        onDone(`Пароль установлен, но письмо на ${member.email} не ушло — передайте доступ лично`, 'error');
      } else if (sendEmail) {
        onDone(`Пароль установлен и отправлен на ${member.email}`, 'success');
      } else {
        onDone(`Пароль для ${member.email} установлен — передайте его лично`, 'success');
      }
    } catch (err) {
      setError((err as Error).message || 'Не удалось установить пароль');
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4 md:p-8"
      onClick={onClose}
    >
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
          <h3 className="text-base font-heading font-semibold text-foreground-900">Задать пароль</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer"
            aria-label="Закрыть"
          >
            <i className="ri-close-line text-foreground-500" />
          </button>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <p className="text-sm text-foreground-600">
            Участник: <span className="font-medium text-foreground-900">{member.name}</span>{' '}
            <span className="text-foreground-500">({member.email})</span>
          </p>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Новый пароль</label>
            <div className="flex gap-2">
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="flex-1 px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
              <button
                type="button"
                onClick={() => setPassword(generatePassword())}
                title="Сгенерировать другой"
                className="px-3 py-2.5 rounded-xl bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line" />
              </button>
              <button
                type="button"
                onClick={copy}
                title="Скопировать"
                className="px-3 py-2.5 rounded-xl bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className={copied ? 'ri-check-line text-emerald-600' : 'ri-file-copy-line'} />
              </button>
            </div>
            <p className="text-xs text-foreground-500 mt-1.5">
              Пароль виден только сейчас — в системе он хранится зашифрованным и посмотреть его позже нельзя.
              Запишите или скопируйте.
            </p>
          </div>

          <label className="flex items-center gap-2 text-sm text-foreground-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer"
            />
            Отправить участнику письмо со ссылкой, логином и этим паролем
          </label>

          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm">{error}</div>
          )}

          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer flex items-center gap-1.5"
            >
              {saving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-key-2-line" />}
              Установить пароль
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
